/* Account switcher: "All" or one Account, searchable (fleets run to hundreds of
   Accounts). Shared by Statistics and the Dashboard; `required` (no "All") by the
   Mass messages pages. The Chat assistant page opens the same menu from its header. */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { C } from './ChatAssistant/compact/tokens';
import { font as f } from './ChatAssistant/compact/style';
import { Icon } from './ChatAssistant/compact/kit';
import { chip, useCloseOnEscape } from './pickerKit';
import { PlatformLogo, type Platform } from './PlatformLogo';

export interface PickerAccount {
  account_id: string;
  name: string;
  /** Searched too, and shown beside the name. */
  username?: string | null;
  /** Shows the platform logo on the row, to tell same-named Accounts apart. */
  platform?: Platform;
}

export function AccountPicker({ accounts, value, onChange, align = 'right', required = false }: { accounts: PickerAccount[]; value?: string; onChange: (id: string | undefined) => void; /** Which edge the menu lines up with. */ align?: 'left' | 'right'; /** One Account must be chosen: no "All". */ required?: boolean }) {
  const { t } = useTranslation('statsUnified');
  const [open, setOpen] = useState(false);
  const current = accounts.find((a) => a.account_id === value);
  return (
    <div style={{ position: 'relative' }}>
      <button type="button" style={chip} className="cpt-hover-border" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        {current?.platform ? <PlatformLogo platform={current.platform} size={13} /> : <Icon name="users" size={13} color={C.muted} />}
        <span style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>{current ? current.name : required ? t('accounts.search') : t('accounts.all')}</span>
        {!current && !required && <span style={{ color: C.muted }}>{accounts.length}</span>}
        <Icon name="chevron-down" size={12} color={C.muted} />
      </button>
      {open && <AccountPickerMenu accounts={accounts} value={value} onChange={onChange} onClose={() => setOpen(false)} align={align} required={required} />}
    </div>
  );
}

/** The searchable list on its own, for a caller with its own trigger (the Account header on the
 *  Chat assistant page). Place it inside a `position: relative` box; it opens `top` px below. */
export function AccountPickerMenu({ accounts, value, onChange, onClose, align = 'right', required = false, top = 36 }: { accounts: PickerAccount[]; value?: string; onChange: (id: string | undefined) => void; onClose: () => void; align?: 'left' | 'right'; required?: boolean; top?: number }) {
  const { t } = useTranslation('statsUnified');
  const [q, setQ] = useState('');
  const input = useRef<HTMLInputElement>(null);
  useCloseOnEscape(true, onClose);
  useEffect(() => { setTimeout(() => input.current?.focus(), 0); }, []);
  const list = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return accounts.filter((a) => !needle || a.name.toLowerCase().includes(needle) || (a.username ?? '').toLowerCase().includes(needle)).slice(0, 200);
  }, [accounts, q]);
  const row = (active: boolean): React.CSSProperties => ({
    display: 'flex', alignItems: 'center', gap: 8, minHeight: 30, padding: '4px 8px', border: 0, borderRadius: 6,
    background: active ? C.tint16 : 'transparent', color: active ? C.text : 'var(--om-body)', font: f(500, 12.5), textAlign: 'left', cursor: 'pointer',
  });
  const pick = (id: string | undefined) => { onChange(id); onClose(); };
  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 20 }} />
      <div style={{ position: 'absolute', top, [align]: 0, zIndex: 21, width: 260, maxWidth: 'calc(100vw - 32px)', padding: 4, background: C.card, border: `1px solid ${C.borderHi}`, borderRadius: 8, boxShadow: '0 12px 32px rgba(0,0,0,.5)', display: 'flex', flexDirection: 'column', gap: 2 }}>
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', margin: 4 }}>
          <Icon name="search" size={12} color={C.muted} style={{ position: 'absolute', left: 9, pointerEvents: 'none' }} />
          <input id="stx-account-search" ref={input} value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('accounts.search')} style={{ width: '100%', height: 28, padding: '0 10px 0 28px', background: C.input, border: `1px solid ${C.borderHi}`, borderRadius: 6, color: C.text, font: f(500, 12), outline: 'none', boxSizing: 'border-box' }} />
        </div>
        <div className="cpt-thin-scroll" style={{ maxHeight: 300, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
          {!required && (
            <button type="button" className="cpt-hover-row" style={row(!value)} onClick={() => pick(undefined)}>
              <span style={{ flex: 1 }}>{t('accounts.all')}</span>
              <span style={{ color: C.muted }}>{accounts.length}</span>
            </button>
          )}
          {list.map((a) => (
            <button key={a.account_id} type="button" className="cpt-hover-row" style={row(a.account_id === value)} onClick={() => pick(a.account_id)}>
              {a.platform && <PlatformLogo platform={a.platform} size={13} />}
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {a.name}
                {a.username && a.username !== a.name && <span style={{ color: C.muted }}> @{a.username}</span>}
              </span>
              {a.account_id === value && <Icon name="check" size={13} color={C.purpleLt} />}
            </button>
          ))}
          {!list.length && <span style={{ padding: '8px', font: f(400, 12), color: C.muted }}>{t('accounts.none')}</span>}
        </div>
      </div>
    </>
  );
}
